import { useCallback } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { PurchaseFormConfirmed_Sport } from '@sorare/core/src/protos/webview/channel/native_messages';

type Purchase = {
  objectId: string;
  sport: Sport;
  monetaryAmount: MonetaryAmount;
  fiatPayment: boolean;
};

const sportToProtoSport = (sport: Sport) => {
  switch (sport) {
    case Sport.FOOTBALL:
      return PurchaseFormConfirmed_Sport.FOOTBALL;
    case Sport.BASEBALL:
      return PurchaseFormConfirmed_Sport.BASEBALL;
    case Sport.NBA:
      return PurchaseFormConfirmed_Sport.NBA;
    default:
      return PurchaseFormConfirmed_Sport.UNRECOGNIZED;
  }
};

export const usePurchaseFormConfirmedMessage = () => {
  const { isIosApp, postMessage } = useIsMobileApp();
  const getInteractionContext = useGetInteractionContext();
  const {
    flags: { sendPurchaseFormPostMessage },
  } = useFeatureFlags();

  return useCallback(
    ({ objectId, sport, monetaryAmount, fiatPayment }: Purchase) => {
      if (!isIosApp || !sendPurchaseFormPostMessage) return;

      postMessage('purchaseFormConfirmed', {
        offerId: objectId,
        ethAmount: monetaryAmount.eth,
        // 0.831: VAT ratio
        eurAmount: Math.round(monetaryAmount.eurCents * 0.831) / 100,
        fiatPayment,
        interactionContext: getInteractionContext(),
        sport: sportToProtoSport(sport),
      });
    },
    [isIosApp, sendPurchaseFormPostMessage, postMessage, getInteractionContext]
  );
};
